import React, { useCallback, useMemo, useState } from "react";

import { EDSDatePicker } from "../../../../../components/eds/EDSDatePicker";
import {
  EnquiryStatus,
  FilterOptions,
  FacetCounts
} from "../../../../../types/enquiries";
import { User } from "../../../../../types/VenueGroup";
import {
  formatDateToFilterValue,
  parseFilterValueToDate
} from "../../../../../utils/date";
import { STATUS_CONFIG } from "../constants";

export interface EnquiriesSidebarProps {
  filters: FilterOptions;
  facetCounts: FacetCounts | null;
  users: User[];
  updateFilter: (
    key: keyof FilterOptions,
    value: FilterOptions[keyof FilterOptions]
  ) => void;
  batchUpdateFilters: (updates: Partial<FilterOptions>) => void;
}

export const EnquiriesSidebar: React.FC<EnquiriesSidebarProps> = ({
  filters,
  facetCounts,
  users,
  updateFilter,
  batchUpdateFilters
}) => {
  const [userSearchQuery, setUserSearchQuery] = useState("");

  const filteredUsers = useMemo(() => {
    if (!userSearchQuery.trim()) return users;
    const query = userSearchQuery.toLowerCase();
    return users.filter((user) => user.full_name.toLowerCase().includes(query));
  }, [users, userSearchQuery]);

  const selectedUserIds = useMemo(() => {
    if (!filters.assigned_to) return [];
    return filters.assigned_to.split(",").filter(Boolean);
  }, [filters.assigned_to]);

  const handleAssignedToSelect = useCallback(
    (userId: string) => {
      const current = filters.assigned_to
        ? filters.assigned_to.split(",").filter(Boolean)
        : [];
      const updated = current.includes(userId)
        ? current.filter((id) => id !== userId)
        : [...current, userId];
      updateFilter(
        "assigned_to",
        updated.length > 0 ? updated.join(",") : undefined
      );
    },
    [filters.assigned_to, updateFilter]
  );

  const handleClearAssignedTo = useCallback(() => {
    updateFilter("assigned_to", undefined);
    setUserSearchQuery("");
  }, [updateFilter]);

  const handleStatusFilterClick = useCallback(
    (status: EnquiryStatus) => {
      const currentStatuses = filters.status ?? [];
      const newStatuses = currentStatuses.includes(status)
        ? currentStatuses.filter((s) => s !== status)
        : [...currentStatuses, status];
      updateFilter("status", newStatuses.length > 0 ? newStatuses : undefined);
    },
    [filters.status, updateFilter]
  );

  const handleDateFromSelect = useCallback(
    (date: Date | undefined) => {
      if (!date) {
        updateFilter("date_from", undefined);
        return;
      }
      const dateString = formatDateToFilterValue(date);

      // If date_to is set and is before the new date_from, batch-clear it
      if (filters.date_to && filters.date_to < dateString) {
        batchUpdateFilters({ date_from: dateString, date_to: undefined });
      } else {
        updateFilter("date_from", dateString);
      }
    },
    [filters.date_to, updateFilter, batchUpdateFilters]
  );

  const handleDateToSelect = useCallback(
    (date: Date | undefined) => {
      if (!date) {
        updateFilter("date_to", undefined);
        return;
      }
      const dateString = formatDateToFilterValue(date);

      // If date_from is set and is after the new date_to, batch-clear it
      if (filters.date_from && filters.date_from > dateString) {
        batchUpdateFilters({ date_to: dateString, date_from: undefined });
      } else {
        updateFilter("date_to", dateString);
      }
    },
    [filters.date_from, updateFilter, batchUpdateFilters]
  );

  const handleClearDateRange = useCallback(() => {
    batchUpdateFilters({ date_from: undefined, date_to: undefined });
  }, [batchUpdateFilters]);

  const dateFrom = filters.date_from
    ? parseFilterValueToDate(filters.date_from)
    : undefined;
  const dateTo = filters.date_to
    ? parseFilterValueToDate(filters.date_to)
    : undefined;

  const hasDateFilter = Boolean(filters.date_from ?? filters.date_to);

  return (
    <div className="flex flex-col h-full overflow-y-auto">
      <div className="mx-4 mt-4 mb-4">
        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
          Status
        </h3>
        {(
          [
            "new",
            "in_progress",
            "complete",
            "cancelled",
            "lost",
            "deleted"
          ] as EnquiryStatus[]
        ).map((status) => (
          <button
            key={status}
            className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-sm mb-1 transition-colors ${
              filters.status?.includes(status)
                ? "bg-accent text-accent-foreground font-medium"
                : "hover:bg-accent/50"
            }`}
            onClick={() => handleStatusFilterClick(status)}
          >
            <span>{STATUS_CONFIG[status].label}</span>
            <span className="px-2 py-0.5 rounded-full bg-muted text-muted-foreground text-xs">
              {facetCounts?.status[status] ?? 0}
            </span>
          </button>
        ))}
      </div>

      <div className="p-4 border-t border-border">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            Date Range
          </h3>
          {hasDateFilter && (
            <button
              className="text-xs text-muted-foreground hover:text-foreground transition-colors min-h-6 min-w-6 p-1"
              onClick={handleClearDateRange}
              aria-label="Clear date range filter"
            >
              Clear
            </button>
          )}
        </div>
        <div className="flex flex-col gap-2">
          <EDSDatePicker
            date={dateFrom}
            onSelect={handleDateFromSelect}
            placeholder="From"
            label="From"
            id="enquiries-date-from"
            className="w-full"
          />
          <EDSDatePicker
            date={dateTo}
            onSelect={handleDateToSelect}
            placeholder="To"
            label="To"
            id="enquiries-date-to"
            className="w-full"
          />
        </div>
      </div>

      <div className="p-4 border-t border-border">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            Assigned to
          </h3>
          {filters.assigned_to && (
            <button
              className="text-xs text-muted-foreground hover:text-foreground transition-colors min-h-6 min-w-6 p-1"
              onClick={handleClearAssignedTo}
              aria-label="Clear assigned to filter"
            >
              Clear
            </button>
          )}
        </div>
        <div className="mb-2">
          <input
            type="text"
            value={userSearchQuery}
            onChange={(e) => setUserSearchQuery(e.target.value)}
            placeholder="Search users..."
            aria-label="Search users"
            className="w-full px-3 py-1.5 rounded-md text-sm border border-border bg-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
        <div
          className="flex flex-col max-h-48 overflow-y-auto"
          aria-multiselectable="true"
          aria-label="Assigned to filter options"
        >
          {filteredUsers.map((user) => (
            <button
              key={user._id}
              className={`w-full flex items-center px-3 py-2 rounded-md text-sm mb-1 transition-colors text-left ${
                selectedUserIds.includes(user._id)
                  ? "bg-accent text-accent-foreground font-medium"
                  : "hover:bg-accent/50"
              }`}
              onClick={() => handleAssignedToSelect(user._id)}
            >
              <span>{user.full_name}</span>
            </button>
          ))}
          {filteredUsers.length === 0 && (
            <p className="text-sm text-muted-foreground px-3 py-2">
              No users found
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
